// MainContent.js
import React from 'react';
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Breadcrumbs, Typography, Link, useTheme } from '@mui/material';
import { NavigateNext as NavigateNextIcon, Home as HomeIcon } from '@mui/icons-material';

const routeNames = {
  '': 'Dashboard',
  'clients': 'Clientes',
  'contacts': 'Contactos',
  'proposals': 'Propuestas',
  'contracts': 'Contratos',
  'projects': 'Proyectos',
  'invoices': 'Facturas',
  'chat': 'Chat',
  'companies': 'Empresas',
  'settings': 'Configuración',
  'help': 'Ayuda',
  'admin': 'Admin',
  'users': 'Usuarios'
};

const generateBreadcrumbs = (pathname) => {
  const paths = pathname.split('/').filter(Boolean);
  if (paths.length === 0) return [];
  
  let breadcrumbs = [{ label: 'Dashboard', path: '/', icon: true }];
  let currentPath = '';
  
  paths.forEach((path, index) => {
    currentPath += `/${path}`;
    let label = routeNames[path] || path.charAt(0).toUpperCase() + path.slice(1);
    if (path.length > 20 || /^\d+$/.test(path)) {
      label = `#${path.substring(0, 8)}...`;
    }
    breadcrumbs.push({ label, path: currentPath, isLast: index === paths.length - 1 });
  });

  return breadcrumbs;
};

const MainContent = () => {
  const location = useLocation();
  const breadcrumbs = generateBreadcrumbs(location.pathname);
  const theme = useTheme();

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      overflow: 'hidden',
    }}>
      {breadcrumbs.length > 0 && (
        <Box sx={{ 
          mb: { xs: 1.5, sm: 2, md: 3 },
          px: { xs: 0.5, sm: 1 },
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
        }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ 
              '& .MuiBreadcrumbs-ol': { 
                flexWrap: 'nowrap',
                whiteSpace: 'nowrap',
              },
              '& .MuiBreadcrumbs-li': {
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
              }
            }}
          >
            {breadcrumbs.map((crumb, index) => (
              crumb.isLast ? (
                <Typography 
                  key={crumb.path} 
                  color="text.primary" 
                  fontWeight={500} 
                  fontSize={{ xs: '0.8rem', sm: '0.9rem' }}
                  sx={{ 
                    wordBreak: 'break-word',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    maxWidth: { xs: '200px', sm: 'none' },
                  }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.path}
                  component={RouterLink}
                  to={crumb.path}
                  underline="hover"
                  color="text.secondary"
                  fontSize={{ xs: '0.75rem', sm: '0.85rem' }}
                  sx={{ 
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {crumb.icon && <HomeIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                  {crumb.label}
                </Link>
              )
            ))}
          </Breadcrumbs>
        </Box>
      )}
      <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainContent;